// src/Views/FastQuotePage.js
import React, { useContext } from 'react';
import { StoreContext } from '../contexts/StoreContext';
import PortfolioGallery from './PortfolioGallery';
import ContactForm from './ContactForm';
import TestimonialCard from './TestimonialCard';
import usePageView from '../hooks/usePageView';
import {
  SPACING_TIGHT,
  SPACING_COMPACT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_SECTION_LARGE,
} from '../constants/layout';
import {
  FONT_TITLE,
  FONT_TITLE_MOBILE,
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_HEADLINE,
  FONT_HEADLINE_MOBILE,
  FONT_BODY,
  FONT_SUBHEADLINE,
} from '../constants/typography';

export default function FastQuotePage() {
  usePageView('welcome');
  const { store } = useContext(StoreContext);
  const phoneNumber = store?.storeContact?.phoneNumber?.trim();
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^+\d]/g, '')}` : '';

  const callButtonContent = phoneNumber ? (
    <>
      <span className="cta-call-button__label">Call Now for a Fast Quote</span>
      <span className="cta-call-button__phone">{phoneNumber}</span>
    </>
  ) : (
    <span className="cta-call-button__label">Call Now for a Fast Quote</span>
  );

  const CallButton = () => (
    phoneHref ? (
      <a href={phoneHref} className="cta-call-button">{callButtonContent}</a>
    ) : (
      <span className="cta-call-button cta-call-button--disabled">{callButtonContent}</span>
    )
  );

  return (
    <div className="fast-quote-page">
      <section className="section section--hero">
        <div className="container">
          <h1 className="page-heading">Why Your Floors Still Look Dirty After Mopping</h1>
          <p className="lede">
            If your floors stay dull after mopping, dirt is trapped deep inside the surface.
          </p>
          <p className="lede">
            We use professional extraction to remove that buildup and make your floors look new again.
          </p>
          <CallButton />
        </div>
      </section>

      <section className="section section--muted">
        <div className="container">
          <div className="gallery-wrap">
            <PortfolioGallery pager viewMorePath="/ourwork" />
          </div>
          <CallButton />
          <p className="reassurance">
            As a specialized family business, if we are not 100% sure we can make your floors look like new,
            we won't take the job. Call to see if your floor type qualifies.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-heading">What Our Clients Have To Say</h2>
          <div className="testimonial-stack">
            <TestimonialCard
              name="Hamid G."
              location="Mission Viejo, CA"
              text={<>"Jonathan and his father helped us clean and polish our 2000 SF travertine flooring. The <mark className="highlight">floors look great</mark>, and we received <mark className="highlight">many compliments from friends and neighbors</mark>…"</>}
            />
            <TestimonialCard
              name="Juan M."
              location="Mission Viejo, CA"
              text={<>"Jose and Jonathan, his son did an awesome job at my home. They cleaned, buffed and polished all the travertine floor downstairs. I have <mark className="highlight">never seen our floor more shiny</mark>. My wife and I are very happy with the work they did and will <mark className="highlight">highly recommend them to neighbors and friends</mark>. Very satisfied with their work."</>}
            />
          </div>
        </div>
      </section>

      <section className="section section--callback">
        <div className="container">
          <h2 className="section-heading">Prefer A Call Back?</h2>
          <p className="callback-intro">Fill the form below, and a member of our team will be in touch.</p>
          <ul className="benefits">
            <li>
              <span className="material-icons benefits-icon" aria-hidden="true">check</span>
              <span>Remove years of dirt and grime in ONE day</span>
            </li>
            <li>
              <span className="material-icons benefits-icon" aria-hidden="true">check</span>
              <span>Make your floors look new</span>
            </li>
            <li>
              <span className="material-icons benefits-icon" aria-hidden="true">check</span>
              <span>Fast one-day turnaround</span>
            </li>
          </ul>
          <div className="form-wrap">
            <ContactForm />
          </div>
          <p className="closing">Call now for a quick 3-minute over-the-phone estimate.</p>
        </div>
      </section>

      <style jsx>{`
        .fast-quote-page {
          width: 100%;
        }

        .section {
          padding: ${SPACING_SECTION_LARGE} 0;
          border-bottom: 1px solid var(--color-border);
        }

        .section:last-of-type {
          border-bottom: none;
        }

        .section--surface {
          background-color: var(--color-surface);
          color: var(--color-text-primary);
        }

        .section--muted {
          background-color: var(--color-surface-muted);
          color: var(--color-text-primary);
        }

        .section--callback {
          background:
            radial-gradient(circle at 15% 20%, rgba(59, 130, 246, 0.10) 0%, rgba(59, 130, 246, 0) 50%),
            radial-gradient(circle at 85% 80%, rgba(245, 158, 11, 0.08) 0%, rgba(245, 158, 11, 0) 45%),
            linear-gradient(180deg, var(--color-surface) 0%, var(--color-surface-muted) 100%);
          color: var(--color-text-primary);
        }

        .section--hero {
          padding-top: ${SPACING_SECTION_LARGE};
          background:
            radial-gradient(ellipse at 20% 0%, rgba(22, 163, 74, 0.10) 0%, transparent 55%),
            radial-gradient(ellipse at 90% 10%, rgba(59, 130, 246, 0.08) 0%, transparent 55%),
            linear-gradient(180deg, var(--color-surface) 0%, var(--color-surface-muted) 100%);
          color: var(--color-text-primary);
        }

        .container {
          max-width: 720px;
          margin: 0 auto;
          padding: 0 clamp(16px, 4vw, 28px);
          padding-left: max(clamp(16px, 4vw, 28px), env(safe-area-inset-left));
          padding-right: max(clamp(16px, 4vw, 28px), env(safe-area-inset-right));
        }

        .page-heading {
          font-size: 2.4rem;
          font-weight: 800;
          color: var(--color-text-primary);
          text-align: center;
          line-height: 1.1;
          letter-spacing: 0;
          margin: 0 auto ${SPACING_CARD} auto;
          max-width: 840px;
        }

        .lede {
          font-size: ${FONT_HEADLINE};
          font-weight: 500;
          color: var(--color-text-secondary);
          text-align: center;
          line-height: 1.5;
          letter-spacing: 0.02em;
          margin: 0 auto ${SPACING_COMPACT} auto;
          max-width: 840px;
        }

        .lede + .lede {
          margin-bottom: ${SPACING_GROUP};
        }

        .cta-call-button {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          justify-content: center;
          gap: 0.4em;
          width: 100%;
          max-width: 480px;
          margin: ${SPACING_GROUP} auto 0 auto;
          padding: ${SPACING_BUTTON} ${SPACING_CARD_BODY};
          background-color: #16a34a;
          color: #ffffff;
          font-size: ${FONT_HEADLINE};
          text-align: center;
          text-decoration: none;
          border-radius: 9999px;
          box-shadow: 0 2px 6px rgba(22, 163, 74, 0.25);
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }

        .cta-call-button__label {
          font-weight: 500;
        }

        .cta-call-button__phone {
          font-weight: 700;
        }

        .cta-call-button:hover,
        .cta-call-button:focus {
          background-color: #15803d;
          color: #ffffff;
          box-shadow: 0 3px 10px rgba(22, 163, 74, 0.35);
        }

        .cta-call-button:active {
          background-color: #166534;
          color: #ffffff;
        }

        .cta-call-button--disabled {
          opacity: 0.6;
          pointer-events: none;
        }

        .gallery-wrap {
          margin: 0 auto ${SPACING_SECTION} auto;
        }

        .reassurance {
          font-style: italic;
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          text-align: center;
          line-height: 1.6;
          max-width: 600px;
          margin: ${SPACING_SECTION} auto 0 auto;
        }

        .section-heading {
          font-size: ${FONT_TITLE2};
          font-weight: 700;
          color: var(--color-text-primary);
          text-align: center;
          line-height: 1.3;
          margin: 0 0 ${SPACING_GROUP} 0;
        }

        .testimonial-stack {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_GROUP};
          max-width: 560px;
          margin: 0 auto;
        }

        .section--callback .section-heading {
          margin-bottom: ${SPACING_COMPACT};
        }

        .callback-intro {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          text-align: center;
          line-height: 1.6;
          margin: 0 auto ${SPACING_GROUP} auto;
          max-width: 560px;
        }

        .benefits {
          list-style: none;
          padding-left: 0;
          margin: 0 auto ${SPACING_GROUP} auto;
          max-width: 480px;
        }

        .benefits li {
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          line-height: 1.6;
          padding: ${SPACING_TIGHT} 0;
          display: flex;
          align-items: center;
          gap: 0.625rem;
        }

        .benefits-icon {
          color: #16a34a;
          font-size: 1.25rem;
          line-height: 1;
          flex-shrink: 0;
        }

        .form-wrap {
          max-width: 520px;
          margin: 0 auto;
        }

        .closing {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          text-align: center;
          line-height: 1.6;
          margin: ${SPACING_SECTION} auto 0 auto;
          max-width: 560px;
        }

        @media (max-width: 768px) {
          .section {
            padding: ${SPACING_SECTION} 0;
          }

          .page-heading {
            font-size: clamp(2rem, 8vw, 2.35rem);
          }

          .lede {
            font-size: ${FONT_HEADLINE_MOBILE};
          }

          .section-heading {
            font-size: ${FONT_TITLE2_MOBILE};
          }

          .cta-call-button {
            margin-top: ${SPACING_GROUP};
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
            font-size: ${FONT_BODY};
            gap: 0.3em;
            width: fit-content;
            max-width: 100%;
          }

          .reassurance {
            margin-top: ${SPACING_GROUP};
          }

          .gallery-wrap {
            margin-bottom: ${SPACING_GROUP};
          }
        }
      `}</style>
    </div>
  );
}
